import { Link } from 'react-router-dom';
import {
  SparklesIcon,
  ArrowRightIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useEndpoints } from '../hooks/useEndpoints';
import { useCollaborations } from '../hooks/useCollaborations';
import { useFlag } from '../hooks/useFeatureFlags';
import HowItWorksStrip from '../components/shared/HowItWorksStrip';
import { formatRelativeTime } from '../utils/dateUtils';
import { endpointDisplayName } from '../utils/endpointLabel';
import type { Collaboration, Endpoint } from '../types';
import { serviceRunPath } from '../components/runExplorer/runExplorerLinks';

/**
 * Adaptive Home landing page (task 115).
 *
 * Renders one of three states derived entirely from existing frontend hooks —
 * no new backend. The state is a threshold over `useEndpoints` (how many apps
 * exist) and `useCollaborations` (how much run history exists):
 *
 *   - invitation  — no endpoints yet → lead with Composer
 *   - apps        — endpoints exist, little/no run history → cards + "compose another"
 *   - operational — meaningful run volume → failures first, recent runs, app cards
 *
 * Thresholds live in the single HOME_STATE_THRESHOLDS block below so they can be
 * tuned in one place. A dedicated innovate-service summary endpoint is a later,
 * separate task; this version deliberately reuses the list hooks.
 */

// ---------------------------------------------------------------------------
// State-selection thresholds — the one place to tune Home's behavior.
// ---------------------------------------------------------------------------
const HOME_STATE_THRESHOLDS = {
  /** How many collaborations to fetch to assess run volume / recency. */
  RUN_SAMPLE_SIZE: 20,
  /** How many endpoints to fetch for app cards. */
  ENDPOINT_SAMPLE_SIZE: 12,
  /**
   * At or above this many total runs, the page becomes operational (state 3).
   * Below it (but with endpoints), it shows the "apps" view (state 2).
   */
  OPERATIONAL_MIN_RUNS: 5,
  /** How many recent runs to surface in the activity strip / operational list. */
  RECENT_RUNS_DISPLAY: 5,
  /** How many failures to surface at the top of the operational view. */
  FAILURES_DISPLAY: 5,
} as const;

const FAILURE_STATUSES = new Set(['failed', 'error']);

type HomeState = 'invitation' | 'apps' | 'operational';

function isFailure(status: string): boolean {
  return FAILURE_STATUSES.has(status.toLowerCase());
}

function statusBadgeClass(status: string): string {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
    case 'running':
      return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
    case 'failed':
    case 'error':
      return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
    case 'waiting':
    case 'waiting_for_input':
      return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
    case 'pending':
    default:
      return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
  }
}

/**
 * Resolve the deep link into a run. We can link straight into the owning app's
 * Runs tab when the collaboration's endpoint relation is populated; otherwise
 * we fall back to the global collaboration detail page.
 */
function runLink(collab: Collaboration): string {
  const endpointId = collab.endpointRequest?.endpointId;
  if (endpointId) {
    return serviceRunPath(endpointId, collab.id);
  }
  return `/collaborations/${collab.id}`;
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

function ComposerCta({ variant }: { variant: 'hero' | 'banner' }) {
  if (variant === 'hero') {
    return (
      <Link
        to="/composer"
        data-testid="home-composer-cta"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-base font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800 shadow-sm transition-colors"
      >
        <SparklesIcon className="h-5 w-5" />
        Start composing
        <ArrowRightIcon className="h-4 w-4" />
      </Link>
    );
  }
  return (
    <Link
      to="/composer"
      data-testid="home-composer-cta"
      className="group flex items-center justify-between gap-4 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 px-5 py-4 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
    >
      <div className="flex items-center gap-3">
        <SparklesIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Compose another</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Describe what you want in plain language — we build the capability for you.
          </p>
        </div>
      </div>
      <ArrowRightIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 group-hover:translate-x-1 transition-transform" />
    </Link>
  );
}

function methodBadgeClass(method: string): string {
  switch (method) {
    case 'GET':
      return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
    case 'POST':
      return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
    case 'PUT':
      return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
    case 'PATCH':
      return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
    case 'DELETE':
      return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  }
}

function AppCard({ endpoint }: { endpoint: Endpoint }) {
  const label = endpointDisplayName(endpoint);
  return (
    <div
      className="group relative flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 hover:shadow-md transition-shadow"
      data-testid="home-app-card"
    >
      <Link to={`/endpoints/${endpoint.id}`} className="block flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${methodBadgeClass(endpoint.method)}`}
          >
            {endpoint.method}
          </span>
          <span className="text-sm font-mono text-gray-700 dark:text-gray-300 truncate">
            {endpoint.path}
          </span>
        </div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 line-clamp-1">
          {label}
        </h3>
        {endpoint.description && endpoint.description.trim() !== label && (
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
            {endpoint.description}
          </p>
        )}
        <p className="mt-2 text-xs font-mono text-gray-500 dark:text-gray-500">
          {endpoint.method} {endpoint.path}
        </p>
      </Link>
      <div className="mt-4">
        <Link
          to={`/endpoints/${endpoint.id}?tab=test`}
          data-testid="home-app-run"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800 transition-colors"
        >
          <PlayIcon className="h-4 w-4" />
          Run
        </Link>
      </div>
    </div>
  );
}

function RecentRunRow({ collab }: { collab: Collaboration }) {
  const endpoint = collab.endpointRequest?.endpoint;
  return (
    <Link
      to={runLink(collab)}
      data-testid="home-recent-run"
      className="flex items-center justify-between gap-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(collab.status)}`}
        >
          {collab.status}
        </span>
        {endpoint ? (
          <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
            {endpointDisplayName(endpoint)}
          </span>
        ) : (
          <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {collab.id.slice(0, 8)}…
          </span>
        )}
      </div>
      <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
        {formatRelativeTime(collab.updatedAt)}
      </span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// State 1 — invitation (no endpoints, or endpoints flag off)
// ---------------------------------------------------------------------------

function InvitationState({ libraryLink }: { libraryLink: string }) {
  return (
    <div className="max-w-3xl mx-auto text-center" data-testid="home-state-invitation">
      <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 dark:bg-indigo-900/40 mb-6">
        <SparklesIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
      </div>
      <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
        What would you like AI to do?
      </h1>
      <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
        Describe an outcome in plain language and Innovate composes a working capability you can use
        anywhere.
      </p>
      <div className="mb-12">
        <ComposerCta variant="hero" />
      </div>

      <div className="text-left mb-8">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4 text-center">
          How Innovate works
        </h2>
        <HowItWorksStrip testId="home-how-it-works" />
      </div>

      <Link
        to={libraryLink}
        data-testid="home-library-link"
        className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline underline-offset-2"
      >
        Prefer to build by hand? Open the Library
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// State 2 — apps (endpoints exist, little/no run history)
// ---------------------------------------------------------------------------

function AppsState({
  endpoints,
  recentRuns,
}: {
  endpoints: Endpoint[];
  recentRuns: Collaboration[];
}) {
  return (
    <div className="max-w-6xl mx-auto" data-testid="home-state-apps">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Your apps
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Open one to test it, see how to call it, or review what it has done.
        </p>
      </div>

      <div className="mb-8">
        <ComposerCta variant="banner" />
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {endpoints.map((endpoint) => (
          <AppCard key={endpoint.id} endpoint={endpoint} />
        ))}
      </div>

      {recentRuns.length > 0 && (
        <div className="mt-10" data-testid="home-recent-activity">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
            Recent activity
          </h2>
          <div className="space-y-2">
            {recentRuns.slice(0, HOME_STATE_THRESHOLDS.RECENT_RUNS_DISPLAY).map((collab) => (
              <RecentRunRow key={collab.id} collab={collab} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// State 3 — operational (meaningful run volume)
// ---------------------------------------------------------------------------

function OperationalState({
  endpoints,
  recentRuns,
  failures,
}: {
  endpoints: Endpoint[];
  recentRuns: Collaboration[];
  failures: Collaboration[];
}) {
  return (
    <div className="max-w-6xl mx-auto" data-testid="home-state-operational">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">Home</h1>
        <p className="text-gray-600 dark:text-gray-400">
          What your AI has been doing — and anything that needs attention.
        </p>
      </div>

      {failures.length > 0 ? (
        <div className="mb-8" data-testid="home-failures">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-red-600 dark:text-red-400 mb-3">
            <ExclamationTriangleIcon className="h-4 w-4" />
            Needs attention
          </h2>
          <div className="space-y-2">
            {failures.slice(0, HOME_STATE_THRESHOLDS.FAILURES_DISPLAY).map((collab) => (
              <Link
                key={collab.id}
                to={runLink(collab)}
                data-testid="home-failure-run"
                className="flex items-center justify-between gap-3 rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 px-4 py-3 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                    {collab.status}
                  </span>
                  {collab.endpointRequest?.endpoint ? (
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                      {endpointDisplayName(collab.endpointRequest.endpoint)}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {collab.error || `${collab.id.slice(0, 8)}…`}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                  {formatRelativeTime(collab.updatedAt)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div
          className="mb-8 flex items-center gap-2 rounded-md border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-900/20 px-4 py-3 text-sm text-green-800 dark:text-green-200"
          data-testid="home-all-clear"
        >
          <CheckCircleIcon className="h-5 w-5" />
          Everything is running smoothly — no recent failures.
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2" data-testid="home-recent-activity">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
            Recent runs
          </h2>
          <div className="space-y-2">
            {recentRuns.slice(0, HOME_STATE_THRESHOLDS.RECENT_RUNS_DISPLAY).map((collab) => (
              <RecentRunRow key={collab.id} collab={collab} />
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
            Your apps
          </h2>
          <div className="space-y-3">
            {endpoints.map((endpoint) => (
              <Link
                key={endpoint.id}
                to={`/endpoints/${endpoint.id}`}
                className="block rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {endpointDisplayName(endpoint)}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${methodBadgeClass(endpoint.method)}`}
                  >
                    {endpoint.method}
                  </span>
                  <span className="text-xs font-mono text-gray-500 dark:text-gray-400 truncate">
                    {endpoint.path}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10 max-w-xl">
        <ComposerCta variant="banner" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function HomePage() {
  const endpointsEnabled = useFlag('innovate.fe.endpoints.page');
  const agentsEnabled = useFlag('innovate.fe.agents.page');

  // The Library entry: prefer Agents (the most sensible "build by hand" door),
  // falling back to the endpoints list if Agents is gated off.
  const libraryLink = agentsEnabled ? '/agents' : '/endpoints';

  const { data: endpointsData, isLoading: endpointsLoading } = useEndpoints(
    1,
    HOME_STATE_THRESHOLDS.ENDPOINT_SAMPLE_SIZE,
  );
  const { data: collabsData, isLoading: collabsLoading } = useCollaborations(
    1,
    HOME_STATE_THRESHOLDS.RUN_SAMPLE_SIZE,
  );

  const endpoints: Endpoint[] = endpointsData?.data ?? [];
  const recentRuns: Collaboration[] = collabsData?.data ?? [];
  const totalRuns = collabsData?.pagination?.total ?? recentRuns.length;
  const failures = recentRuns.filter((c) => isFailure(c.status));

  // If the endpoints feature is off, there is nothing to surface as apps —
  // fall back to the invitation state without endpoint affordances.
  const endpointsUsable = endpointsEnabled;

  const isLoading = endpointsLoading || collabsLoading;

  // Derive the state. Thresholds are centralized in HOME_STATE_THRESHOLDS.
  let state: HomeState;
  if (!endpointsUsable || endpoints.length === 0) {
    state = 'invitation';
  } else if (totalRuns >= HOME_STATE_THRESHOLDS.OPERATIONAL_MIN_RUNS) {
    state = 'operational';
  } else {
    state = 'apps';
  }

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8" data-testid="home-page">
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500 dark:text-gray-400">Loading…</div>
        </div>
      ) : state === 'invitation' ? (
        <InvitationState libraryLink={libraryLink} />
      ) : state === 'operational' ? (
        <OperationalState endpoints={endpoints} recentRuns={recentRuns} failures={failures} />
      ) : (
        <AppsState endpoints={endpoints} recentRuns={recentRuns} />
      )}
    </div>
  );
}
